import { CSSProperties } from 'react';
import './AnimatedLogo.css';
import paperBridgeIcon from '../assets/paperbridge-icon.png';

type AnimatedLogoProps = {
  /** Pixel side length of the bounding box. Defaults to 26 (sidebar size). */
  size?: number;
  /** Extra class names on the outer container, if the caller needs them. */
  className?: string;
};

/**
 * PaperBridge brand mark with a continuous, embedded animation.
 *
 * A soft green "scan line" sweeps top→bottom across the mark every ~3.4 s
 * (the OCR / extraction metaphor); the mark brightens subtly while the
 * line passes; a tiny sparkle pops at the top-right just after the scan
 * exits (the "info extracted" beat). The bounding box never moves and
 * the mark never deforms, so it's safe to drop into dense chrome.
 *
 * No props beyond size — drop-in replacement for the static `<img>` tag.
 * Respects `prefers-reduced-motion` (animations disable, mark stays still).
 */
export function AnimatedLogo({ size = 26, className }: AnimatedLogoProps): JSX.Element {
  const style: CSSProperties = {
    // CSS custom property → consumed by AnimatedLogo.css.
    ['--pb-logo-size' as never]: `${size}px`,
  };
  return (
    <span
      className={`pb-animated-logo${className ? ` ${className}` : ''}`}
      style={style}
      aria-hidden="true"
    >
      <img src={paperBridgeIcon} alt="" draggable={false} />
      <span className="pb-animated-logo-sparkle" />
    </span>
  );
}
